/**
 * Finds the documents browser ("Contents") a file preview belongs to. A
 * preview is routed under its owner's page, so the owner comes from the
 * preview's own route and its root folder is read when the preview opens.
 */
import { TReportType } from "../types/reporting.types";
import { reportTypeFromPath } from "../utils/reporting/reportRoute";
import { getOperations } from "./api-server/operations";
import { getWells } from "./api-server/wells";
import { fetchReportById } from "./reporting-hub/fetchReports";

/** The record whose page hosts the documents browser. */
export type DocumentsOwner =
	| { kind: "operation"; operationCode: string }
	| { kind: "well"; wellId: string }
	| { kind: "report"; reportType: TReportType; reportId: string };

/** The browser's root folder and the tenant whose container holds it. */
export interface DocumentsRoot {
	tenantKey: string;
	folderId: string;
}

/**
 * The owner of a legacy preview route, or null when its page has no documents
 * browser to link back to. Deliverable files sit in the operation's folder
 * tree, but the operation's browser only lists the operation's own files.
 */
export const documentsOwnerFromRoute = (
	params: Readonly<Record<string, string | undefined>>,
	pathname: string
): DocumentsOwner | null => {
	if (params.reportId) {
		const reportType = reportTypeFromPath(pathname);
		return reportType
			? { kind: "report", reportType, reportId: params.reportId }
			: null;
	}
	if (params.wellcode) return { kind: "well", wellId: params.wellcode };
	if (params.operationname && !params.taskname) {
		return { kind: "operation", operationCode: params.operationname };
	}
	return null;
};

/** Reads the owner's documents root, or null when it has none yet. */
export const resolveDocumentsRoot = async (
	tenantKey: string,
	owner: DocumentsOwner
): Promise<DocumentsRoot | null> => {
	switch (owner.kind) {
		case "operation": {
			const operations: any = await getOperations(tenantKey, {
				operation_code: owner.operationCode,
			});
			const folderId = operations?.[0]?.folder_id;
			return folderId ? { tenantKey, folderId } : null;
		}
		case "well": {
			const wells: any = await getWells(tenantKey, { _id: owner.wellId });
			const folderId = wells?.[0]?.folder_id;
			return folderId ? { tenantKey, folderId } : null;
		}
		case "report": {
			// Report files live in the operator's container, not the viewer's.
			const report = await fetchReportById(owner.reportType, owner.reportId);
			return report?.folder_id && report?.operator
				? { tenantKey: report.operator, folderId: report.folder_id }
				: null;
		}
	}
};
